import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { API_BASE } from "../config/api";
import "./profile.css";
import {
  generateMembershipCard,
  getMyMembership,
} from "../services/membershipService";

const Profile = () => {
  const [editMode, setEditMode] = useState(false);
  const [user, setUser] = useState({
    name: "",
    email: "",
    phone: "",
    department: "",
    year: "",
    rollNo: "",
    address: "",
  });

  const [membership, setMembership] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetch(`${API_BASE}/api/user/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setUser(data);
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    getMyMembership()
      .then((data) => setMembership(data))
      .catch(() => {});
  }, [token]);

  const handleChange = (e) =>
    setUser({ ...user, [e.target.name]: e.target.value });

  const handleSave = async () => {
    const res = await fetch(`${API_BASE}/api/user/profile`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(user),
    });

    if (res.ok) {
      const data = await res.json();
      setUser(data);
      setEditMode(false);
    }
  };

  const membershipStatus = !membership
    ? { text: "Not Applied", color: "pending", icon: "⏳" }
    : membership.status === "approved"
    ? { text: "Verified Member", color: "approved", icon: "✓" }
    : membership.status === "rejected"
    ? { text: "Application Denied", color: "rejected", icon: "✕" }
    : { text: "Under Review", color: "pending", icon: "⏳" };

  const handleCardAction = async () => {
    try {
      const data = await generateMembershipCard();
      window.open(`${API_BASE}${data.cardUrl}`, "_blank");
    } catch (err) {
      alert(err.message || "Unable to generate membership card");
    }
  };

  return (
    <>
      <Navbar />

      <div className="profile-page-premium">
        {/* ANIMATED BACKGROUND */}
        <div className="profile-background">
          <div className="bg-glow glow-1" />
          <div className="bg-glow glow-2" />
          <div className="bg-glow glow-3" />
        </div>

        <div className="profile-container">
          {/* PREMIUM HERO PROFILE SECTION */}
          <section className="profile-hero">
            <div className="hero-background" />

            <div className="profile-hero-content">
              {/* PROFILE AVATAR */}
              <div className="profile-avatar-container">
                <div className="avatar-frame">
                  <div className="avatar-glow-ring" />
                  <img src="/images/logo.png" alt={user.name} className="profile-avatar" />
                  <div className="avatar-spotlight" />
                </div>

                {/* STATUS BADGE */}
                <div className={`status-badge-premium ${membershipStatus.color}`}>
                  <span className="badge-icon">{membershipStatus.icon}</span>
                  <span className="badge-text">{membershipStatus.text}</span>
                  {membershipStatus.color === "approved" && <div className="badge-pulse" />}
                </div>
              </div>

              {/* HERO IDENTITY INFO */}
              <div className="hero-info">
                <div className="hero-eyebrow">Your Leadership Profile</div>
                <h1 className="hero-name">{user.name || "Member Name"}</h1>
                <p className="hero-email">{user.email || "email@example.com"}</p>
                <p className="hero-subtitle">
                  {user.department && user.year
                    ? `${user.department} • ${user.year}`
                    : "YUVA Member"}
                </p>

                <div className="hero-divider" />

                {/* ACTION BUTTONS */}
                <div className="hero-actions">
                  <button
                    className={`action-button ${editMode ? "save" : "edit"}`}
                    onClick={editMode ? handleSave : () => setEditMode(true)}
                  >
                    <span className="button-icon">
                      {editMode ? "💾" : "✏️"}
                    </span>
                    <span className="button-text">
                      {editMode ? "Save Changes" : "Customize Identity"}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* INFORMATION SECTIONS */}
          <div className="profile-sections">
            {/* PERSONAL INFORMATION */}
            <section className="premium-card personal-section">
              <div className="section-header">
                <h2 className="section-title">Personal Information</h2>
                <div className="section-accent" />
              </div>

              <div className="information-grid">
                <div className="info-field">
                  <label className="field-label">Full Name</label>
                  <input
                    name="name"
                    value={user.name || ""}
                    disabled={!editMode}
                    onChange={handleChange}
                    className="field-input"
                  />
                </div>

                <div className="info-field">
                  <label className="field-label">Registration Number</label>
                  <input
                    name="rollNo"
                    value={user.rollNo || ""}
                    disabled={!editMode}
                    onChange={handleChange}
                    className="field-input"
                  />
                </div>

                <div className="info-field">
                  <label className="field-label">Email Address</label>
                  <input
                    value={user.email || ""}
                    disabled
                    className="field-input"
                  />
                </div>

                <div className="info-field">
                  <label className="field-label">Phone Number</label>
                  <input
                    name="phone"
                    value={user.phone || ""}
                    disabled={!editMode}
                    onChange={handleChange}
                    className="field-input"
                  />
                </div>

                <div className="info-field">
                  <label className="field-label">Department</label>
                  <input
                    name="department"
                    value={user.department || ""}
                    disabled={!editMode}
                    onChange={handleChange}
                    className="field-input"
                  />
                </div>

                <div className="info-field">
                  <label className="field-label">Year</label>
                  <input
                    name="year"
                    value={user.year || ""}
                    disabled={!editMode}
                    onChange={handleChange}
                    className="field-input"
                  />
                </div>

                <div className="info-field full-width">
                  <label className="field-label">Address</label>
                  <input
                    name="address"
                    value={user.address || ""}
                    disabled={!editMode}
                    onChange={handleChange}
                    className="field-input"
                  />
                </div>
              </div>
            </section>

            {/* MEMBERSHIP CREDENTIAL */}
            <section className="premium-card membership-section">
              <div className="section-header">
                <h2 className="section-title">Membership Credential</h2>
                <div className="section-accent" />
              </div>

              {!membership ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <h3>No Active Membership</h3>
                  <p>You haven't applied for YUVA membership yet. Apply now to unlock exclusive access.</p>
                </div>
              ) : (
                <div className="membership-credential">
                  {/* CREDENTIAL CARD */}
                  <div className="credential-card">
                    <div className="credential-background" />
                    <div className="credential-shimmer" />

                    <div className="credential-inner">
                      {/* HEADER */}
                      <div className="credential-header">
                        <div className="credential-badge">
                          <span className="badge-seal">★</span>
                        </div>
                        <div className="credential-title">YUVA Credential</div>
                      </div>

                      {/* INFO ROWS */}
                      <div className="credential-info">
                        <div className="credential-row">
                          <span className="credential-label">Member ID</span>
                          <span className="credential-value">{membership.membershipId}</span>
                        </div>
                        <div className="credential-row">
                          <span className="credential-label">Status</span>
                          <span className={`credential-status ${membership.status}`}>
                            {membership.status.charAt(0).toUpperCase() + membership.status.slice(1)}
                          </span>
                        </div>
                        <div className="credential-row">
                          <span className="credential-label">Valid Until</span>
                          <span className="credential-value">
                            {new Date(membership.validTill).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>

                      {/* FOOTER */}
                      <div className="credential-footer">
                        <p className="credential-tagline">Elite Member of YUVA Ecosystem</p>
                      </div>
                    </div>

                    {/* ACCENT LIGHTS */}
                    <div className="credential-accent accent-1" />
                    <div className="credential-accent accent-2" />
                  </div>

                  {/* ACTION */}
                  {membership.status === "approved" && (
                    <button className="premium-cta-button" onClick={handleCardAction}>
                      <span className="cta-icon">📥</span>
                      <span className="cta-text">Access Digital Credential</span>
                      <span className="cta-arrow">→</span>
                    </button>
                  )}

                  {membership.status !== "approved" && (
                    <div className="status-info-box">
                      <p>Your digital credential will be available after membership approval.</p>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Profile;
